import { EDGE_OPERATIONS } from "./geometry";
import { ExpressionSyntaxError, expressionPaths } from "./pattern-coordinate-map";
import { expandTransforms } from "./transforms";
import type { AssertionDefinition, BlockDefinition, ChoiceDefinition, Diagnostic, EdgeDefinition, EdgeListEntry, MarkListEntry, OptionDefinition, PatternSpecification, PointReference, ValueReference } from "./types";

const VALUE_TYPES = new Set(["length", "integer", "number", "toggle"]);
const DIRECTIONS = new Set(["up", "down", "left", "right"]);
const TANGENTS = new Set(["horizontal", "vertical"]);
const MARK_OPERATIONS = new Set(["foldMark", "lineMark", "notch", "distributeButtons", "button", "buttonhole"]);
const GRAIN_DIRECTIONS = new Set(["vertical", "horizontal", "bias"]);
const VALUE_KEYS = ["measurement", "option", "constant", "derived", "expression"] as const;
/** Choice keys are read in expressions as options.<id>.<key>, so they must be identifiers. */
const CHOICE_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Check a parsed specification before drafting.
 *
 * Errors mark problems that stop the draft or silently change it (unknown
 * operations, missing fields, out-of-range defaults). Warnings mark dangling
 * references; the draft still runs and skips whatever they touch, so a
 * half-finished spec keeps drawing.
 */
export function validateSpecification(value: unknown): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  if (!value || typeof value !== "object") {
    return [{ severity: "error", code: "SPEC_OBJECT", message: "The YAML root must be an object." }];
  }
  const spec = value as Partial<PatternSpecification>;
  if (spec.schemaVersion !== 3) {
    diagnostics.push({ severity: "error", code: "SCHEMA_VERSION", message: "Only schemaVersion 3 is supported." });
  }
  if (!spec.pattern?.id || !spec.pattern.name || !spec.pattern.version) {
    diagnostics.push({ severity: "error", code: "PATTERN_METADATA", message: "Pattern id, name, and version are required." });
  }
  if (spec.pattern?.category !== undefined && typeof spec.pattern.category !== "string") {
    diagnostics.push({ severity: "error", code: "PATTERN_METADATA", message: "Pattern category must be a string." });
  }
  const makes = spec.pattern?.makes as unknown;
  if (makes !== undefined && (!Array.isArray(makes) || makes.some((garment) => typeof garment !== "string" || garment.trim() === ""))) {
    diagnostics.push({ severity: "error", code: "PATTERN_METADATA", message: "Pattern makes must be a list of garment names." });
  }
  const sizeRange = spec.pattern?.sizeRange as unknown;
  if (sizeRange !== undefined && !["baby", "child", "adult"].includes(sizeRange as string)) {
    diagnostics.push({ severity: "error", code: "PATTERN_METADATA", message: "Pattern sizeRange must be baby, child or adult." });
  }
  if (spec.pattern?.units !== "mm") {
    diagnostics.push({ severity: "error", code: "PATTERN_UNITS", message: "Pattern units must be mm." });
  }
  if ((spec as { modules?: unknown }).modules !== undefined) {
    diagnostics.push({ severity: "error", code: "MODULES_UNRESOLVED", message: "modules must be resolved first: load the specification with parseSpecification and a module registry." });
  }
  if (!spec.blocks || typeof spec.blocks !== "object" || Object.keys(spec.blocks).length === 0) {
    diagnostics.push({ severity: "error", code: "BLOCKS_REQUIRED", message: "At least one draft block is required." });
    return diagnostics;
  }
  new SpecificationChecker(expandTransforms(spec), diagnostics).run();
  return diagnostics;
}

class SpecificationChecker {
  private readonly measurements: Set<string>;
  private readonly options: Set<string>;
  private readonly constants: Set<string>;
  private readonly derived = new Set<string>();
  private readonly points = new Set<string>();
  /** Edge IDs as written in a block, which is how derived values name them. */
  private readonly bareEdges = new Set<string>();
  /** Edge IDs qualified by block, which is how drafted edges are named. */
  private readonly qualifiedEdges = new Set<string>();
  private readonly blocks: [string, BlockDefinition][];

  constructor(private readonly spec: Partial<PatternSpecification>, private readonly diagnostics: Diagnostic[]) {
    this.measurements = new Set(Object.keys(spec.measurements ?? {}));
    this.options = new Set(Object.keys(spec.options ?? {}));
    this.constants = new Set(Object.keys(spec.constants ?? {}));
    this.blocks = Object.entries(spec.blocks ?? {});
    for (const [blockId, block] of this.blocks) {
      for (const [id, definition] of Object.entries(block?.derivedValues ?? {})) {
        if (definition?.operation === "pointAlongEdges" || definition?.operation === "edgeCrossing") {
          this.derived.add(`${id}X`);
          this.derived.add(`${id}Y`);
        } else {
          this.derived.add(id);
        }
      }
      for (const edgeId of Object.keys(block?.edges ?? {})) {
        this.bareEdges.add(edgeId);
        this.qualifiedEdges.add(`${blockId}.${edgeId}`);
      }
    }
  }

  run() {
    this.checkValueDefinitions("measurement", this.spec.measurements, false);
    this.checkValueDefinitions("option", this.spec.options, true);
    for (const [id, constant] of Object.entries(this.spec.constants ?? {})) {
      if (typeof constant !== "number" || !Number.isFinite(constant)) this.error("CONSTANT_VALUE", `Constant ${id} must be a finite number.`);
    }
    for (const [blockId, block] of this.blocks) this.collectPoints(blockId, block);
    for (const [blockId, block] of this.blocks) {
      this.checkOperations(blockId, block);
      this.checkTransforms(blockId, block);
      this.checkDerivedValues(blockId, block);
      this.checkEdges(blockId, block);
      this.checkMarks(blockId, block);
      this.checkPieces(blockId, block);
    }
    this.checkSeamComparisons();
    this.checkAssertions();
    this.checkPresets();
  }

  private error(code: string, message: string) {
    this.diagnostics.push({ severity: "error", code, message });
  }

  private warning(code: string, message: string) {
    this.diagnostics.push({ severity: "warning", code, message });
  }

  private checkValueDefinitions(kind: string, definitions: Record<string, OptionDefinition> | undefined, allowChoice: boolean) {
    for (const [id, definition] of Object.entries(definitions ?? {})) {
      if (!definition || typeof definition !== "object") {
        this.error("VALUE_DEFINITION", `${kind} ${id} must be an object.`);
        continue;
      }
      if (definition.type === "choice") {
        if (!allowChoice) this.error("VALUE_TYPE", `${kind} ${id} cannot be a choice; only options can.`);
        else this.checkChoice(id, definition);
        continue;
      }
      if (!VALUE_TYPES.has(definition.type)) {
        this.error("VALUE_TYPE", `${kind} ${id} has unknown type "${definition.type}".`);
      }
      if (typeof definition.default !== "number" || !Number.isFinite(definition.default)) {
        this.error("VALUE_DEFAULT", `${kind} ${id} needs a numeric default.`);
        continue;
      }
      const { minimum, maximum } = definition;
      if (minimum !== undefined && maximum !== undefined && minimum > maximum) {
        this.error("VALUE_RANGE", `${kind} ${id} has a minimum above its maximum.`);
      }
      if ((minimum !== undefined && definition.default < minimum) || (maximum !== undefined && definition.default > maximum)) {
        this.error("VALUE_RANGE", `${kind} ${id} default ${definition.default} is outside ${minimum ?? "-∞"}–${maximum ?? "∞"}.`);
      }
    }
  }

  private checkChoice(id: string, definition: ChoiceDefinition) {
    const choices = definition.choices;
    if (!choices || typeof choices !== "object" || Array.isArray(choices) || Object.keys(choices).length === 0) {
      this.error("CHOICE_DEFINITION", `option ${id} needs choices: a map of keys to labels.`);
      return;
    }
    for (const [key, label] of Object.entries(choices)) {
      if (!CHOICE_KEY.test(key)) this.error("CHOICE_DEFINITION", `option ${id} choice "${key}" must be an identifier (letters, digits, underscores).`);
      if (typeof label !== "string" || label === "") this.error("CHOICE_DEFINITION", `option ${id} choice ${key} needs a label.`);
    }
    if (typeof definition.default !== "string" || !Object.hasOwn(choices, definition.default)) {
      this.error("VALUE_DEFAULT", `option ${id} default must be one of its choices: ${Object.keys(choices).join(", ")}.`);
    }
  }

  private choice(id: string): ChoiceDefinition | undefined {
    const definition = this.spec.options?.[id];
    return definition?.type === "choice" ? definition : undefined;
  }

  /** `when` must be an expression; it may read measurements, options, constants and points. */
  private checkCondition(where: string, when: unknown) {
    if (when === undefined) return;
    if (typeof when !== "string" || when.trim() === "") {
      this.error("CONDITION", `${where}: when must be an expression string.`);
      return;
    }
    this.checkExpression(where, "when", when);
    try {
      if (expressionPaths(when).some((path) => path.startsWith("derived."))) {
        this.warning("CONDITION", `${where}: when cannot read derived values; they are measured after conditions are applied.`);
      }
    } catch { /* syntax already reported */ }
  }

  /**
   * Every edge an edge list can name, whichever conditions are on, with group
   * shape, conditions and outline parts checked on the way.
   */
  private edgeListNames(where: string, list: unknown, block: BlockDefinition, depth = 0): string[] {
    if (!Array.isArray(list)) {
      this.error("INVALID_FIELD", `${where} needs a list of edges.`);
      return [];
    }
    return list.flatMap((entry: EdgeListEntry): string[] => {
      if (typeof entry === "string") return [entry];
      if (depth > 16) return [];
      if (entry && typeof entry === "object" && "part" in entry) {
        if (typeof entry.part !== "string" || !block.outlineParts?.[entry.part]) {
          this.warning("UNKNOWN_PART", `${where} uses unknown outline part ${String(entry.part)}.`);
          return [];
        }
        return this.edgeListNames(where, block.outlineParts[entry.part], block, depth + 1);
      }
      if (entry && typeof entry === "object" && "edges" in entry) {
        if (entry.when === undefined) this.error("CONDITION", `${where}: an edge group needs when.`);
        this.checkCondition(where, entry.when);
        return this.edgeListNames(where, entry.edges, block, depth + 1);
      }
      this.error("INVALID_FIELD", `${where}: an edge list entry must be an edge ID, { when, edges } or { part }.`);
      return [];
    });
  }

  private markListNames(where: string, list: unknown, block: BlockDefinition, depth = 0): string[] {
    if (!Array.isArray(list)) {
      this.error("INVALID_FIELD", `${where} needs a list of marks.`);
      return [];
    }
    return list.flatMap((entry: MarkListEntry): string[] => {
      if (typeof entry === "string") return [entry];
      if (depth > 16) return [];
      if (entry && typeof entry === "object" && "part" in entry) {
        if (typeof entry.part !== "string" || !block.markParts?.[entry.part]) {
          this.warning("UNKNOWN_PART", `${where} uses unknown mark part ${String(entry.part)}.`);
          return [];
        }
        return this.markListNames(where, block.markParts[entry.part], block, depth + 1);
      }
      if (entry && typeof entry === "object" && "marks" in entry) {
        if (entry.when === undefined) this.error("CONDITION", `${where}: a mark group needs when.`);
        this.checkCondition(where, entry.when);
        return this.markListNames(where, entry.marks, block, depth + 1);
      }
      this.error("INVALID_FIELD", `${where}: a mark list entry must be a mark ID, { when, marks } or { part }.`);
      return [];
    });
  }

  private collectPoints(blockId: string, block: BlockDefinition) {
    if (!Array.isArray(block?.operations)) {
      this.error("OPERATIONS_REQUIRED", `Block ${blockId} needs an operations list.`);
      return;
    }
    for (const operation of block.operations) {
      if (!operation?.id) {
        this.error("POINT_ID", `${blockId} contains an operation without an id.`);
        continue;
      }
      if (this.points.has(operation.id)) this.error("DUPLICATE_ID", `Duplicate point id ${operation.id}.`);
      this.points.add(operation.id);
    }
  }

  private checkOperations(blockId: string, block: BlockDefinition) {
    for (const operation of block.operations ?? []) {
      if (!operation?.id) continue;
      const where = `${blockId}.${operation.id}`;
      const field = (name: string) => (operation as Record<string, unknown>)[name];
      switch (operation.operation) {
        case "absolutePoint":
          this.checkValue(where, "x", field("x"));
          this.checkValue(where, "y", field("y"));
          break;
        case "directionalOffset":
          this.checkPoint(where, "from", field("from"));
          this.checkDirection(where, field("direction"));
          this.checkValue(where, "distance", field("distance"));
          break;
        case "alignedPoint":
          this.checkAxis(where, "x", field("x"), field("xFrom"));
          this.checkAxis(where, "y", field("y"), field("yFrom"));
          break;
        case "pointOnLine":
          this.checkPoint(where, "from", field("from"));
          this.checkPoint(where, "to", field("to"));
          this.checkValue(where, "value", field("value"));
          if (field("by") !== undefined && field("by") !== "ratio" && field("by") !== "distance") {
            this.error("INVALID_FIELD", `${where}: by must be "ratio" or "distance".`);
          }
          break;
        case "midpointByX":
          this.checkPointPair(where, "between", field("between"));
          this.checkAxis(where, "y", field("y"), field("yFrom"));
          break;
        case "perpendicularOffset":
          this.checkPoint(where, "from", field("from"));
          this.checkPointPair(where, "relativeTo", field("relativeTo"));
          this.checkValue(where, "distance", field("distance"));
          break;
        case "mirrorPoint":
          this.checkPoint(where, "from", field("from"));
          this.checkPointPair(where, "axis", field("axis"));
          break;
        case "shoulderEndpoint": {
          this.checkPoint(where, "from", field("from"));
          this.checkPointPair(where, "lengthFrom", field("lengthFrom"));
          const guide = field("towardConstructionLine") as Record<string, unknown> | undefined;
          if (!guide || typeof guide !== "object") {
            this.error("MISSING_FIELD", `${where} needs towardConstructionLine.`);
            break;
          }
          this.checkPoint(where, "towardConstructionLine.from", guide.from);
          this.checkDirection(where, guide.direction);
          if (guide.offset !== undefined) this.checkValue(where, "towardConstructionLine.offset", guide.offset);
          break;
        }
        case "rotatePoint": {
          this.checkPoint(where, "from", field("from"));
          this.checkPoint(where, "around", field("around"));
          const opening = field("opening") as Record<string, unknown> | undefined;
          if (opening !== undefined) {
            if (!opening || typeof opening !== "object") {
              this.error("INVALID_FIELD", `${where}: opening must be an object.`);
              break;
            }
            this.checkPoint(where, "opening.point", opening.point);
            this.checkValue(where, "opening.distance", opening.distance);
            if (opening.direction !== "clockwise" && opening.direction !== "anticlockwise") {
              this.error("INVALID_FIELD", `${where}: opening.direction must be clockwise or anticlockwise.`);
            }
          } else {
            this.checkValue(where, "angle", field("angle"));
          }
          break;
        }
        case "transformPoint":
          this.checkPoint(where, "from", field("from"));
          this.checkTransformName(where, block, field("transform"));
          break;
        default:
          this.error("UNKNOWN_OPERATION", `${where} uses unknown point operation "${operation.operation}".`);
      }
    }
  }

  private checkTransformName(where: string, block: BlockDefinition, name: unknown) {
    if (typeof name !== "string" || name === "") this.error("MISSING_FIELD", `${where} needs transform.`);
    else if (!block.transforms?.[name]) this.error("UNKNOWN_TRANSFORM", `${where} uses unknown transform ${name}; transforms belong to the same block.`);
  }

  private checkTransforms(blockId: string, block: BlockDefinition) {
    const transforms = block.transforms;
    if (transforms === undefined) return;
    if (!transforms || typeof transforms !== "object" || Array.isArray(transforms)) {
      this.error("INVALID_FIELD", `${blockId}: transforms must be a map of transform IDs to definitions.`);
      return;
    }
    for (const [id, transform] of Object.entries(transforms)) {
      const where = `${blockId} transform ${id}`;
      if (!transform || typeof transform !== "object") {
        this.error("INVALID_FIELD", `${where} must be an object.`);
        continue;
      }
      if (typeof transform.suffix !== "string" || !/^[A-Za-z0-9_]+$/.test(transform.suffix)) {
        this.error("MISSING_FIELD", `${where} needs a suffix of letters, digits or underscores, added to each moved point and edge ID.`);
      }
      this.checkCondition(where, transform.when);
      if (transform.points !== undefined && !Array.isArray(transform.points)) this.error("INVALID_FIELD", `${where}: points must be a list of point IDs.`);
      for (const point of Array.isArray(transform.points) ? transform.points : []) this.checkPoint(where, "points", point);
      if (transform.edges !== undefined && !Array.isArray(transform.edges)) this.error("INVALID_FIELD", `${where}: edges must be a list of edge IDs.`);
      for (const edgeId of Array.isArray(transform.edges) ? transform.edges : []) {
        if (!block.edges?.[edgeId]) this.warning("UNKNOWN_EDGE", `${where} moves unknown edge ${edgeId}.`);
      }
      if (transform.meets !== undefined) {
        if (!transform.meets || typeof transform.meets !== "object" || Array.isArray(transform.meets)) this.error("INVALID_FIELD", `${where}: meets must map carried points to the points their images land on.`);
        else {
          const carried = new Set([...(Array.isArray(transform.points) ? transform.points : []), ...(Array.isArray(transform.edges) ? transform.edges : []).flatMap((edgeId) => [block.edges?.[edgeId]?.from, block.edges?.[edgeId]?.to])]);
          for (const [source, target] of Object.entries(transform.meets)) {
            if (!carried.has(source)) this.warning("UNKNOWN_POINT", `${where} meets: ${source} is not a point it carries.`);
            this.checkPoint(where, `meets.${source}`, target);
          }
        }
      }
      if (!transform.points?.length && !transform.edges?.length) this.warning("EMPTY_TRANSFORM", `${where} moves no points or edges.`);
      if ([transform.rotate, transform.translate, transform.align].filter(Boolean).length !== 1) {
        this.error("MISSING_FIELD", `${where} needs one of rotate, translate or align.`);
      } else if (transform.align) {
        if (!transform.align || typeof transform.align !== "object") this.error("INVALID_FIELD", `${where}: align must be { from: [a, b], to: [c, d] }.`);
        else {
          this.checkPointPair(where, "align.from", transform.align.from);
          this.checkPointPair(where, "align.to", transform.align.to);
        }
      } else if (transform.rotate) {
        const rotate = transform.rotate;
        this.checkPoint(where, "rotate.around", rotate.around);
        const ways = [rotate.angle, rotate.close, rotate.opening].filter((way) => way !== undefined).length;
        if (ways !== 1) this.error("MISSING_FIELD", `${where}: rotate needs exactly one of angle, close or opening.`);
        if (rotate.angle !== undefined) this.checkValue(where, "rotate.angle", rotate.angle);
        if (rotate.close !== undefined) this.checkPointPair(where, "rotate.close", rotate.close);
        if (rotate.ratio !== undefined) {
          if (rotate.close === undefined) this.error("INVALID_FIELD", `${where}: rotate.ratio only scales close.`);
          this.checkValue(where, "rotate.ratio", rotate.ratio);
        }
        if (rotate.opening !== undefined) {
          const opening = rotate.opening;
          if (!opening || typeof opening !== "object") this.error("INVALID_FIELD", `${where}: rotate.opening must be an object.`);
          else {
            this.checkPoint(where, "rotate.opening.point", opening.point);
            this.checkValue(where, "rotate.opening.distance", opening.distance);
            if (opening.direction !== "clockwise" && opening.direction !== "anticlockwise") this.error("INVALID_FIELD", `${where}: rotate.opening.direction must be clockwise or anticlockwise.`);
          }
        }
      } else {
        const translate = transform.translate!;
        if (translate.along !== undefined) {
          this.checkPointPair(where, "translate.along", translate.along);
          this.checkValue(where, "translate.distance", translate.distance);
          if (translate.dx !== undefined || translate.dy !== undefined) this.error("INVALID_FIELD", `${where}: translate takes along and distance, or dx and dy, not both.`);
        } else {
          if (translate.dx === undefined && translate.dy === undefined) this.error("MISSING_FIELD", `${where}: translate needs dx and dy, or along and distance.`);
          if (translate.dx !== undefined) this.checkValue(where, "translate.dx", translate.dx);
          if (translate.dy !== undefined) this.checkValue(where, "translate.dy", translate.dy);
        }
      }
      if (transform.follow !== undefined) {
        const seen = [id];
        let next: string | undefined = transform.follow;
        while (next !== undefined) {
          if (!transforms[next]) {
            this.error("UNKNOWN_TRANSFORM", `${where} follows unknown transform ${next}.`);
            break;
          }
          if (seen.includes(next)) {
            this.error("TRANSFORM_LOOP", `${where} follows transforms in a loop: ${[...seen, next].join(" → ")}.`);
            break;
          }
          seen.push(next);
          next = transforms[next].follow;
        }
      }
    }
  }

  private checkDerivedValues(blockId: string, block: BlockDefinition) {
    for (const [id, definition] of Object.entries(block.derivedValues ?? {})) {
      const where = `${blockId}.${id}`;
      this.checkCondition(where, definition?.when);
      if (definition?.operation === "pointAlongEdges") {
        this.checkPoint(where, "from", definition.from);
        this.checkValue(where, "distance", definition.distance);
      } else if (definition?.operation === "edgeCrossing") {
        const lines = [definition.x, definition.y, definition.crossing].filter((line) => line !== undefined).length;
        if (lines !== 1) this.error("MISSING_FIELD", `${where} needs one of x, y or crossing: the line or edges the edges cross.`);
        else if (definition.crossing !== undefined) {
          if (!Array.isArray(definition.crossing) || definition.crossing.length === 0) this.error("MISSING_FIELD", `${where} crossing needs a list of edges.`);
          else for (const edgeId of definition.crossing) if (!this.bareEdges.has(edgeId)) this.warning("UNKNOWN_EDGE", `${where} crosses unknown edge ${edgeId}.`);
        } else this.checkValue(where, definition.x !== undefined ? "x" : "y", definition.x ?? definition.y);
      } else if (definition?.operation !== "edgeLengthSum") {
        this.error("UNKNOWN_OPERATION", `${where} uses unknown derived operation "${definition?.operation}".`);
        continue;
      }
      if (!Array.isArray(definition.edges) || definition.edges.length === 0) {
        this.error("MISSING_FIELD", `${where} needs a list of edges.`);
        continue;
      }
      for (const edgeId of definition.edges) {
        if (!this.bareEdges.has(edgeId)) this.warning("UNKNOWN_EDGE", `${where} sums unknown edge ${edgeId}.`);
      }
    }
  }

  private checkEdges(blockId: string, block: BlockDefinition) {
    for (const [edgeId, edge] of Object.entries(block.edges ?? {})) {
      const where = `${blockId}.${edgeId}`;
      if (!edge || typeof edge !== "object") {
        this.error("EDGE_DEFINITION", `${where} must be an object.`);
        continue;
      }
      this.checkCondition(where, edge.when);
      if (!EDGE_OPERATIONS.has(edge.operation)) {
        this.error("UNKNOWN_EDGE_OPERATION", `${where} uses unknown edge operation "${edge.operation}".`);
      }
      this.checkPoint(where, "from", edge.from);
      this.checkPoint(where, "to", edge.to);
      for (const id of edge.through ?? []) this.checkPoint(where, "through", id);
      if (edge.operation === "edgePortion") {
        const names = edge.edges ?? (edge.edge ? [edge.edge] : []);
        if (names.length === 0) this.error("MISSING_FIELD", `${where} needs edge or edges.`);
        for (const name of names) {
          // A portion of a portion is fine: derived edges are drawn in dependency
          // order. The source may be in another block.
          if (!this.anyEdge(block, name)) this.warning("UNKNOWN_EDGE", `${where} takes a portion of unknown edge ${name}.`);
        }
      }
      if (edge.operation === "mirrorEdge") {
        if (!edge.edge) this.error("MISSING_FIELD", `${where} needs edge: the edge to mirror.`);
        else {
          const source = this.anyEdge(block, edge.edge);
          if (!source) this.warning("UNKNOWN_EDGE", `${where} mirrors unknown edge ${edge.edge}.`);
          else if (source.operation === "mirrorEdge") this.error("INVALID_FIELD", `${where}: a mirrored edge cannot be mirrored again.`);
        }
        this.checkPointPair(where, "axis", edge.axis);
      }
      if (edge.operation === "transformEdge") {
        if (!edge.edge) this.error("MISSING_FIELD", `${where} needs edge: the edge to move.`);
        else if (!this.anyEdge(block, edge.edge)) this.warning("UNKNOWN_EDGE", `${where} moves unknown edge ${edge.edge}.`);
        this.checkTransformName(where, block, edge.transform);
      }
      if (edge.operation === "forkCurve") {
        this.checkPoint(where, "toward", edge.toward);
        if (edge.touch !== undefined) this.checkPoint(where, "touch", edge.touch);
        if (edge.style !== undefined && edge.style !== "front" && edge.style !== "back") {
          this.error("INVALID_FIELD", `${where}: style must be front or back.`);
        }
      }
      if (edge.endHandles !== undefined && edge.endHandles !== "short" && edge.endHandles !== "natural") {
        this.error("INVALID_FIELD", `${where}: endHandles must be short or natural.`);
      }
      this.checkControl(where, "control1", edge.control1);
      this.checkControl(where, "control2", edge.control2);
      if (edge.constraints?.startAlong !== undefined) this.checkPointPair(where, "constraints.startAlong", edge.constraints.startAlong);
      if (edge.constraints?.endAlong !== undefined) this.checkPointPair(where, "constraints.endAlong", edge.constraints.endAlong);
      for (const name of ["startTangent", "endTangent"] as const) {
        const tangent = edge.constraints?.[name];
        if (tangent !== undefined && !TANGENTS.has(tangent)) {
          this.error("INVALID_FIELD", `${where}: ${name} must be "horizontal" or "vertical".`);
        }
      }
    }
  }

  private checkMarks(blockId: string, block: BlockDefinition) {
    const edges = new Set(Object.keys(block.edges ?? {}));
    const ids = new Set<string>();
    for (const mark of block.marks ?? []) {
      const where = `${blockId}.${mark?.id}`;
      if (!mark?.id) {
        this.error("MARK_ID", `${blockId} contains a mark without an id.`);
        continue;
      }
      if (ids.has(mark.id)) this.error("DUPLICATE_ID", `Duplicate mark id ${where}.`);
      ids.add(mark.id);
      this.checkCondition(where, mark.when);
      if (!MARK_OPERATIONS.has(mark.operation)) {
        this.error("UNKNOWN_OPERATION", `${where} uses unknown mark operation "${mark.operation}".`);
        continue;
      }
      if (mark.operation === "notch" || mark.operation === "button") {
        this.checkPoint(where, "at", mark.at);
      } else if (mark.operation === "distributeButtons") {
        if (!mark.along) this.error("MISSING_FIELD", `${where} needs along.`);
        else if (!edges.has(mark.along)) this.warning("UNKNOWN_EDGE", `${where} runs along unknown edge ${mark.along}.`);
        this.checkOptionValue(where, "count", mark.count, true);
        this.checkOptionValue(where, "startPadding", mark.startPadding, false);
        this.checkOptionValue(where, "endPadding", mark.endPadding, false);
      } else {
        this.checkPoint(where, "from", mark.from);
        this.checkPoint(where, "to", mark.to);
      }
    }
  }

  private checkPieces(blockId: string, block: BlockDefinition) {
    const edges = block.edges ?? {};
    const marks = new Set((block.marks ?? []).map((mark) => mark?.id));
    const pieces = block.pieces ?? {};
    for (const [partId, part] of Object.entries(block.outlineParts ?? {})) {
      for (const edgeId of this.edgeListNames(`${blockId} outline part ${partId}`, part, block)) {
        if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${blockId} outline part ${partId} uses unknown edge ${edgeId}.`);
      }
    }
    for (const [partId, part] of Object.entries(block.markParts ?? {})) {
      for (const markId of this.markListNames(`${blockId} mark part ${partId}`, part, block)) {
        if (!marks.has(markId)) this.warning("UNKNOWN_MARK", `${blockId} mark part ${partId} uses unknown mark ${markId}.`);
      }
    }
    for (const [pieceId, piece] of Object.entries(pieces)) {
      const where = `${blockId}.${pieceId}`;
      if (!Array.isArray(piece?.outline) || piece.outline.length === 0) {
        this.error("PIECE_OUTLINE", `${where} needs an outline of edges.`);
        continue;
      }
      this.checkCondition(where, piece.when);
      const outlineNames = this.edgeListNames(where, piece.outline, block);
      for (const edgeId of outlineNames) {
        if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${where} outline uses unknown edge ${edgeId}.`);
      }
      // A conditional outline is checked for gaps when drafted, for the options in use.
      if (piece.outline.every((entry) => typeof entry === "string")) this.checkOutlineContinuity(where, piece.outline as string[], edges);
      if (piece.layoutWith && !pieces[piece.layoutWith]) {
        this.warning("UNKNOWN_PIECE", `${where} lays out with unknown piece ${piece.layoutWith}.`);
      }
      if (piece.bodyOverlay) {
        this.checkCondition(`${where} body overlay`, piece.bodyOverlay.when);
        for (const edgeId of this.edgeListNames(`${where} body overlay`, piece.bodyOverlay.edges ?? [], block)) {
          if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${where} body overlay uses unknown edge ${edgeId}.`);
        }
      }
      const overlays = new Set<string>();
      for (const overlay of piece.referenceOverlays ?? []) {
        const overlayWhere = `${where}.${overlay?.id}`;
        this.checkCondition(overlayWhere, overlay?.when);
        for (const edgeId of this.edgeListNames(overlayWhere, overlay?.edges ?? [], block)) {
          if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${overlayWhere} copies unknown edge ${edgeId}.`);
        }
        this.checkPointPair(overlayWhere, "align.from", overlay?.align?.from);
        this.checkPointPair(overlayWhere, "align.to", overlay?.align?.to);
        if (overlay?.follow && !overlays.has(overlay.follow)) {
          this.warning("UNKNOWN_OVERLAY", `${overlayWhere} follows ${overlay.follow}, which is not an earlier overlay on this piece.`);
        }
        if (overlay?.id) overlays.add(overlay.id);
      }
      for (const edgeId of this.edgeListNames(`${where} internal lines`, piece.internalLines ?? [], block)) {
        if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${where} internal line uses unknown edge ${edgeId}.`);
      }
      for (const markId of this.markListNames(`${where} internal marks`, piece.internalMarks ?? [], block)) {
        if (!marks.has(markId)) this.warning("UNKNOWN_MARK", `${where} uses unknown mark ${markId}.`);
      }
      const outline = new Set(outlineNames);
      // Only unconditional lists are checked for repeats: conditional ones may
      // name an edge twice under conditions that never hold together.
      const plain = (rule: { when?: unknown; edges?: unknown[] } | undefined) => rule?.when === undefined && (rule?.edges ?? []).every((entry) => typeof entry === "string");
      const allowanceEdges = new Set<string>();
      for (const allowance of piece.seamAllowances ?? []) {
        this.checkCondition(`${where} seam allowance`, allowance?.when);
        for (const edgeId of this.edgeListNames(`${where} seam allowance`, allowance?.edges ?? [], block)) {
          if (!outline.has(edgeId)) this.warning("ALLOWANCE_EDGE", `${where} adds seam allowance to ${edgeId}, which is not in its outline.`);
          if (!plain(allowance)) continue;
          if (allowanceEdges.has(edgeId)) this.warning("ALLOWANCE_EDGE", `${where} gives ${edgeId} more than one seam allowance.`);
          allowanceEdges.add(edgeId);
        }
        if (allowance?.included !== undefined && typeof allowance.included !== "boolean") {
          this.error("INVALID_FIELD", `${where}: seamAllowances.included must be true or false.`);
        }
        this.checkOptionValue(where, "seamAllowances.distance", allowance?.distance, true);
      }
      for (const clip of piece.clips ?? []) {
        this.checkCondition(`${where} clip`, clip?.when);
        const along = this.edgeListNames(`${where} clip`, clip?.along ?? [], block);
        if (along.length === 0) this.error("MISSING_FIELD", `${where} clip needs edges to cut along.`);
        for (const edgeId of along) if (!edges[edgeId]) this.warning("UNKNOWN_EDGE", `${where} clips along unknown edge ${edgeId}.`);
        this.checkPoint(`${where} clip`, "away", clip?.away);
        if (clip?.allowance !== undefined) this.checkOptionValue(where, "clips.allowance", clip.allowance, true);
      }
      const inlayEdges = new Set<string>();
      for (const inlay of piece.inlays ?? []) {
        this.checkCondition(`${where} inlay`, inlay?.when);
        for (const edgeId of this.edgeListNames(`${where} inlay`, inlay?.edges ?? [], block)) {
          if (!outline.has(edgeId)) this.warning("INLAY_EDGE", `${where} adds inlay to ${edgeId}, which is not in its outline.`);
          if (!plain(inlay)) continue;
          if (inlayEdges.has(edgeId)) this.warning("INLAY_EDGE", `${where} gives ${edgeId} more than one inlay.`);
          inlayEdges.add(edgeId);
        }
        this.checkOptionValue(where, "inlays.distance", inlay?.distance, true);
      }
      if (piece.print?.grainDirection !== undefined && !GRAIN_DIRECTIONS.has(piece.print.grainDirection)) {
        this.error("INVALID_FIELD", `${where}: grainDirection must be vertical, horizontal or bias.`);
      }
      const cutQuantity = piece.print?.cutQuantity;
      if (cutQuantity !== undefined && (!Number.isInteger(cutQuantity) || cutQuantity < 1)) {
        this.error("INVALID_FIELD", `${where}: cutQuantity must be a whole number of at least 1.`);
      }
    }
  }

  /** Neighbouring outline edges must share an end point, so the cut line closes. */
  private checkOutlineContinuity(where: string, outline: string[], edges: NonNullable<BlockDefinition["edges"]>) {
    const known = outline.map((edgeId) => edges[edgeId]).filter((edge) => edge?.from && edge?.to);
    if (known.length !== outline.length || known.length < 2) return;
    for (let index = 0; index < known.length; index += 1) {
      const current = known[index];
      const next = known[(index + 1) % known.length];
      const shared = [current.from, current.to].some((id) => id === next.from || id === next.to);
      if (!shared) {
        this.warning("OUTLINE_GAP", `${where} outline does not join ${outline[index]} to ${outline[(index + 1) % outline.length]}.`);
      }
    }
  }

  private checkSeamComparisons() {
    for (const [id, comparison] of Object.entries(this.spec.seamComparisons ?? {})) {
      this.checkCondition(`Seam comparison ${id}`, comparison?.when);
      for (const side of [comparison?.first, comparison?.second]) {
        if (!Array.isArray(side?.edges)) {
          this.error("MISSING_FIELD", `Seam comparison ${id} needs edges on both sides.`);
          continue;
        }
        for (const edgeId of side.edges) {
          if (!this.qualifiedEdges.has(edgeId)) this.warning("UNKNOWN_EDGE", `Seam comparison ${id} uses unknown edge ${edgeId}.`);
        }
      }
    }
  }

  private checkAssertions() {
    const assertions = this.spec.assertions;
    if (assertions === undefined) return;
    if (!Array.isArray(assertions)) {
      this.error("ASSERTIONS", "assertions must be a list.");
      return;
    }
    const ids = new Set<string>();
    for (const assertion of assertions as AssertionDefinition[]) {
      if (!assertion?.id) {
        this.error("ASSERTION_ID", "Every assertion needs an id.");
        continue;
      }
      const where = `assertion ${assertion.id}`;
      if (ids.has(assertion.id)) this.error("DUPLICATE_ID", `Duplicate ${where}.`);
      ids.add(assertion.id);
      if (!assertion.label) this.error("MISSING_FIELD", `${where} needs a label.`);
      this.checkCondition(where, assertion.when);
      if (assertion.check === "distance") {
        this.checkPoint(where, "from", assertion.from);
        this.checkPoint(where, "to", assertion.to);
        if (assertion.axis !== undefined && assertion.axis !== "x" && assertion.axis !== "y") {
          this.error("INVALID_FIELD", `${where}: axis must be x or y.`);
        }
      } else if (assertion.check === "edgeLength") {
        if (!Array.isArray(assertion.edges) || assertion.edges.length === 0) {
          this.error("MISSING_FIELD", `${where} needs a list of edges.`);
        } else {
          for (const edgeId of assertion.edges) {
            if (!this.qualifiedEdges.has(edgeId)) this.warning("UNKNOWN_EDGE", `${where} measures unknown edge ${edgeId}.`);
          }
        }
      } else {
        this.error("UNKNOWN_OPERATION", `${where} uses unknown check "${(assertion as { check?: string }).check}".`);
        continue;
      }
      if (assertion.equals === undefined && assertion.minimum === undefined && assertion.maximum === undefined) {
        this.error("ASSERTION_BOUNDS", `${where} needs equals, minimum or maximum.`);
      }
      for (const bound of ["equals", "minimum", "maximum"] as const) {
        if (assertion[bound] !== undefined) this.checkValue(where, bound, assertion[bound]);
      }
      if (assertion.tolerance !== undefined && (typeof assertion.tolerance !== "number" || assertion.tolerance < 0)) {
        this.error("INVALID_FIELD", `${where}: tolerance must be a non-negative number.`);
      }
    }
  }

  private checkPresets() {
    const presets = this.spec.presets;
    if (presets === undefined) return;
    if (!Array.isArray(presets)) {
      this.error("PRESETS", "presets must be a list.");
      return;
    }
    const ids = new Set<string>();
    for (const preset of presets) {
      if (!preset?.id) {
        this.error("PRESET_ID", "Every preset needs an id.");
        continue;
      }
      const where = `preset ${preset.id}`;
      if (ids.has(preset.id)) this.error("DUPLICATE_ID", `Duplicate ${where}.`);
      ids.add(preset.id);
      for (const [option, value] of Object.entries(preset.options ?? {})) {
        const definition = this.spec.options?.[option];
        if (!definition) {
          this.warning("UNKNOWN_REFERENCE", `${where} sets unknown option ${option}.`);
        } else if (definition.type === "choice") {
          if (typeof value !== "string" || !Object.hasOwn(definition.choices ?? {}, value)) {
            this.error("VALUE_RANGE", `${where} sets ${option} to ${String(value)}, which is not one of its choices.`);
          }
        } else if (typeof value !== "number" || (definition.minimum !== undefined && value < definition.minimum) || (definition.maximum !== undefined && value > definition.maximum)) {
          this.error("VALUE_RANGE", `${where} sets ${option} to ${value}, outside ${definition.minimum ?? "-∞"}–${definition.maximum ?? "∞"}.`);
        }
      }
    }
  }

  /** An edge in this block, or else in any block (derived edges may draw from another block). */
  private anyEdge(block: BlockDefinition, name: string): EdgeDefinition | undefined {
    return block.edges?.[name] ?? Object.values(this.spec.blocks ?? {}).map((other) => other?.edges?.[name]).find(Boolean);
  }

  private checkPoint(where: string, field: string, id: unknown) {
    if (typeof id !== "string" || id === "") {
      this.error("MISSING_FIELD", `${where} needs ${field}.`);
      return;
    }
    if (!this.points.has(id)) this.warning("UNKNOWN_POINT", `${where} ${field} refers to unknown point ${id}.`);
  }

  private checkPointPair(where: string, field: string, pair: unknown) {
    if (!Array.isArray(pair) || pair.length !== 2) {
      this.error("MISSING_FIELD", `${where} needs ${field} as a pair of point IDs.`);
      return;
    }
    pair.forEach((id) => this.checkPoint(where, field, id));
  }

  private checkDirection(where: string, direction: unknown) {
    if (typeof direction !== "string" || !DIRECTIONS.has(direction)) {
      this.error("INVALID_FIELD", `${where}: direction must be up, down, left or right.`);
    }
  }

  private checkAxis(where: string, axis: "x" | "y", literal: unknown, from: unknown) {
    if (literal !== undefined) this.checkValue(where, axis, literal);
    else if (from !== undefined) this.checkPoint(where, `${axis}From`, from);
    else this.error("MISSING_FIELD", `${where} needs ${axis} or ${axis}From.`);
  }

  private checkControl(where: string, field: string, control: PointReference | undefined) {
    if (control === undefined || Array.isArray(control)) return;
    if (typeof control === "string") this.checkPoint(where, field, control);
    else if (control.point !== undefined) this.checkPoint(where, field, control.point);
  }

  /** Marks and seam allowances read a number or an option, nothing else. */
  private checkOptionValue(where: string, field: string, value: unknown, required: boolean) {
    if (value === undefined) {
      if (required) this.error("MISSING_FIELD", `${where} needs ${field}.`);
      return;
    }
    if (typeof value === "number") return;
    const option = (value as { option?: unknown })?.option;
    if (typeof option !== "string") {
      this.error("VALUE_REFERENCE", `${where}: ${field} must be a number or { option }.`);
    } else if (!this.options.has(option)) {
      this.warning("UNKNOWN_REFERENCE", `${where} ${field} refers to unknown option ${option}.`);
    } else if (this.choice(option)) {
      this.error("VALUE_REFERENCE", `${where} ${field} reads choice option ${option}, which is not a number.`);
    }
  }

  private checkValue(where: string, field: string, value: unknown) {
    if (value === undefined) {
      this.error("MISSING_FIELD", `${where} needs ${field}.`);
      return;
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value)) this.error("VALUE_REFERENCE", `${where}: ${field} must be finite.`);
      return;
    }
    if (!value || typeof value !== "object") {
      this.error("VALUE_REFERENCE", `${where}: ${field} must be a number or a value reference.`);
      return;
    }
    const reference = value as Exclude<ValueReference, number>;
    const keys = VALUE_KEYS.filter((key) => reference[key] !== undefined);
    if (keys.length !== 1) {
      this.error("VALUE_REFERENCE", `${where}: ${field} needs exactly one of ${VALUE_KEYS.join(", ")}.`);
      return;
    }
    const [key] = keys;
    const name = reference[key];
    if (typeof name !== "string") {
      this.error("VALUE_REFERENCE", `${where}: ${field}.${key} must be a string.`);
      return;
    }
    if (key === "expression") this.checkExpression(where, field, name);
    else this.checkName(where, field, key, name);
  }

  private checkName(where: string, field: string, kind: "measurement" | "option" | "constant" | "derived", name: string) {
    const known = { measurement: this.measurements, option: this.options, constant: this.constants, derived: this.derived }[kind];
    if (!known.has(name)) this.warning("UNKNOWN_REFERENCE", `${where} ${field} refers to unknown ${kind} ${name}.`);
    else if (kind === "option" && this.choice(name)) this.error("VALUE_REFERENCE", `${where} ${field} reads choice option ${name} as a number; use an expression such as options.${name}.<key>.`);
  }

  private checkExpression(where: string, field: string, source: string) {
    let paths: string[];
    try {
      paths = expressionPaths(source);
    } catch (error) {
      if (!(error instanceof ExpressionSyntaxError)) throw error;
      this.error("EXPRESSION_SYNTAX", `${where} ${field}: ${error.message} (${source}).`);
      return;
    }
    const namespaces: Record<string, Set<string>> = {
      measurements: this.measurements,
      options: this.options,
      constants: this.constants,
      derived: this.derived,
    };
    for (const path of paths) {
      const [namespace, name, axis, ...rest] = path.split(".");
      const choice = namespace === "options" ? this.choice(name) : undefined;
      if (choice) {
        if (axis === undefined) this.error("VALUE_REFERENCE", `${where} ${field} reads choice option ${name} as a number; use options.${name}.<key>, one of ${Object.keys(choice.choices ?? {}).join(", ")}.`);
        else if (!Object.hasOwn(choice.choices ?? {}, axis) || rest.length > 0) this.warning("UNKNOWN_REFERENCE", `${where} ${field} reads ${path}, but ${axis} is not one of ${name}'s choices (${Object.keys(choice.choices ?? {}).join(", ")}).`);
        continue;
      }
      const known = namespace === "points"
        ? this.points.has(name) && (axis === "x" || axis === "y") && rest.length === 0
        : namespaces[namespace]?.has(name) && axis === undefined;
      if (!known) this.warning("UNKNOWN_REFERENCE", `${where} ${field} reads unknown value ${path}.`);
    }
  }
}
